import React, { useState } from 'react';
import { useBookCharacters } from '../hooks/useBookCharacters';
import CharacterBottomSheet from './CharacterBottomSheet';
import type { Character } from '../api/characters';
import { motion, LayoutGroup } from 'framer-motion';

interface BookCharactersProps {
  bookId: number;
}

const BookCharacters: React.FC<BookCharactersProps> = ({ bookId }) => {
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);

  const { data, isLoading, error } = useBookCharacters(bookId, {
    enabled: !!bookId,
  });

  const characters = data?.characters || [];

  if (!isLoading && !error && characters.length === 0) {
    return null;
  }

  const handleCharacterClick = (character: Character, event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setClickPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
    setSelectedCharacter(character);
    setIsSheetOpen(true);
  };

  const handleCloseSheet = () => {
    setIsSheetOpen(false);
    setTimeout(() => {
      setSelectedCharacter(null);
      setClickPosition(null);
    }, 300);
  };

  if (isLoading) {
    return (
      <div className="mb-4">
        <h2 className="px-3 text-sm md:text-base font-bold text-foreground mb-3 font-persian">
          شخصیت‌ها
        </h2>
        <div className="flex gap-4 overflow-x-auto scrollbar-hide px-3 pb-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex flex-col items-center flex-shrink-0">
              <div className="w-28 h-28 rounded-full bg-gray-200 animate-pulse"></div>
              <div className="mt-2 w-16 h-3 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  return (
    <LayoutGroup>
      <div className="mb-4">
        <h2 className="px-3 text-sm md:text-base font-bold text-foreground mb-3 font-persian">
          <span data-guide="book-detail-characters" className="inline-block">
          شخصیت‌ها
          </span>
        </h2>
        <div className="flex gap-4 overflow-x-auto scrollbar-hide px-3 pb-2">
          {characters.map((character) => (
            <button
              key={character.character_id}
              onClick={(e) => handleCharacterClick(character, e)}
              className="flex flex-col items-center flex-shrink-0 group cursor-pointer"
              aria-label={`مشاهده جزئیات ${character.name}`}
            >
              {!isSheetOpen || selectedCharacter?.character_id !== character.character_id ? (
                <motion.div
                  layoutId={`character-avatar-${character.character_id}`}
                  className="relative"
                  transition={{ 
                    type: 'spring', 
                    stiffness: 300, 
                    damping: 30 
                  }}
                >
                  <div className="w-28 h-28 rounded-full overflow-hidden border-3 border-gray-200 group-hover:border-primary-400 transition-all duration-200 shadow-lg group-hover:shadow-xl">
                    <img
                      src={character.icon}
                      alt={character.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + character.name;
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 rounded-full bg-primary-500/0 group-hover:bg-primary-500/10 transition-colors duration-200"></div>
                </motion.div>
              ) : (
                <div className="w-28 h-28 rounded-full bg-transparent"></div>
              )}
              <span className="mt-2 text-xs font-persian font-medium text-gray-700 group-hover:text-primary-600 transition-colors text-center max-w-[90px] line-clamp-2">
                {character.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      <CharacterBottomSheet
        character={selectedCharacter}
        isOpen={isSheetOpen}
        onClose={handleCloseSheet}
        clickPosition={clickPosition}
      />
    </LayoutGroup>
  );
};

export default BookCharacters;
